import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";
import api from "@/lib/api";

const AMOUNT_RX = /^\d*\.?\d{0,2}$/;

/** "Help Fuel Startup Otter" — variable-amount support, one-time or monthly.
 *  Sits inside the Founders window on /pricing; no account required. */
export default function DonateCta() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [selected, setSelected] = useState(null);
  const [monthly, setMonthly] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: cfg } = useQuery({
    queryKey: ["donation-config"],
    queryFn: async () => (await api.get("/donations/config")).data,
  });

  if (!cfg?.enabled) return null;
  const value = Number(selected ?? amount);
  const valid = Number.isFinite(value) && value >= cfg.min_amount && value <= cfg.max_amount;

  const pick = (a) => { setSelected(a); setAmount(""); };
  const typeAmount = (v) => {
    if (!AMOUNT_RX.test(v)) return;
    setAmount(v);
    setSelected(null);
  };

  const donate = async () => {
    if (!valid) {
      toast.error(`Enter an amount between $${cfg.min_amount} and $${cfg.max_amount.toLocaleString()}.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: value, recurring: monthly, donor_name: name || null,
        message: note || null, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout.");
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative overflow-hidden border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl lg:p-10"
      data-testid="donate-cta"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#148b8f]/10 blur-3xl" />

      {!open ? (
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#2dd4bf]">
              <Heart className="h-3.5 w-3.5" /> Not ready for a membership?
            </p>
            <h3 className="mt-3 text-2xl font-normal tracking-tight text-white lg:text-3xl" data-testid="donate-title">
              {cfg.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{cfg.blurb}</p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 border border-[#148b8f] bg-[#148b8f]/10 px-8 py-4 font-[640] text-white transition-colors hover:bg-[#148b8f]"
            data-testid="donate-open-button"
          >
            <Heart className="h-4 w-4" /> {cfg.button_label}
          </button>
        </div>
      ) : (
        <div className="relative" data-testid="donate-form">
          <h3 className="text-xl font-normal tracking-tight text-white lg:text-2xl">{cfg.title}</h3>
          <p className="mt-1.5 text-sm text-slate-400">Choose an amount — any size helps.</p>

          <div className="mt-6 flex flex-wrap gap-3">
            {cfg.suggested_amounts.map((a) => (
              <button
                key={a}
                onClick={() => pick(a)}
                className={`min-w-[92px] border px-5 py-3 text-base font-[640] transition-colors ${
                  selected === a
                    ? "border-[#148b8f] bg-[#148b8f] text-white"
                    : "border-white/15 bg-transparent text-white hover:border-[#148b8f]"
                }`}
                data-testid={`donate-amount-${a}`}
              >
                ${a}
              </button>
            ))}
            <div className="flex items-center border border-white/15 bg-transparent px-4 focus-within:border-[#148b8f]">
              <span className="text-base text-slate-400">$</span>
              <input
                value={amount}
                onChange={(e) => typeAmount(e.target.value)}
                inputMode="decimal"
                placeholder="Other"
                className="w-24 bg-transparent px-2 py-3 text-base text-white outline-none placeholder:text-slate-500"
                data-testid="donate-custom-amount"
              />
            </div>
          </div>

          {cfg.allow_monthly && (
            <label className="mt-5 inline-flex cursor-pointer items-center gap-3 text-sm text-slate-300" data-testid="donate-monthly-label">
              <input
                type="checkbox"
                checked={monthly}
                onChange={(e) => setMonthly(e.target.checked)}
                className="h-4 w-4 accent-[#148b8f]"
                data-testid="donate-monthly-toggle"
              />
              Make this a monthly contribution
            </label>
          )}

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              placeholder="Your name (optional)"
              className="border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#148b8f]"
              data-testid="donate-name"
            />
            <input
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 280))}
              placeholder="Note to the team (optional)"
              className="border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#148b8f]"
              data-testid="donate-note"
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={donate}
              disabled={busy || !valid}
              className="inline-flex items-center justify-center gap-2 bg-[#148b8f] px-8 py-4 font-[640] text-white transition-colors hover:bg-[#107073] disabled:opacity-50"
              data-testid="donate-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />}
              {valid
                ? `Give $${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${monthly ? " / month" : ""}`
                : "Choose an amount"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-4 text-sm text-slate-400 transition-colors hover:text-white"
              data-testid="donate-cancel"
            >
              Cancel
            </button>
          </div>

          <p className="mt-4 max-w-xl text-xs leading-relaxed text-slate-500" data-testid="donate-note-text">
            Secure checkout by Stripe. {cfg.note}
          </p>
        </div>
      )}
    </motion.div>
  );
}
